import type { CreateReceiptInput, RequisitionDetail } from '#shared/types';
import { dateKey } from '#client/lib/dates';

type OrderedLine = RequisitionDetail['lines'][number];

/** One ordered reagent, and what the clerk says arrived of it. */
export interface DeliveryLine {
  line_id: number;
  item_id: number;
  label: string;
  ordered_packs: number;
  received_packs: number;
  include: boolean;
  quantity_packs: number;
  lot_no: string;
  expiry_at: string;
  pack_price: number;
}

export interface DeliveryHeader {
  received_at: string;
  invoice_no: string;
  delivery_note: string;
  note: string;
}

export function outstandingPacks(line: { ordered_packs: number; received_packs: number }): number {
  return Math.max(0, line.ordered_packs - line.received_packs);
}

export function initialDeliveryHeader(now: Date): DeliveryHeader {
  return {
    received_at: dateKey(now),
    invoice_no: '',
    delivery_note: '',
    note: '',
  };
}

export function initialDeliveryLines(ordered: readonly OrderedLine[]): DeliveryLine[] {
  return ordered.map((line) => {
    const outstanding = outstandingPacks({
      ordered_packs: line.quantity_packs,
      received_packs: line.received_packs,
    });
    return {
      line_id: line.id,
      item_id: line.item_id,
      label: `${line.item.name} — ${line.item.pack_label}`,
      ordered_packs: line.quantity_packs,
      received_packs: line.received_packs,
      // A line that is already settled starts unticked: the common case for
      // it appearing at all is that something *else* on the order is late.
      include: outstanding > 0,
      quantity_packs: outstanding || line.quantity_packs,
      lot_no: '',
      expiry_at: '',
      pack_price: line.unit_price ?? 0,
    };
  });
}

/** Why the delivery cannot be sent yet, or null when it can. */
/** The delivery lines with one of them, found by its order line, changed. */
export function updateDeliveryLine(
  lines: readonly DeliveryLine[],
  { lineId, changes }: { lineId: number; changes: Partial<DeliveryLine> },
): DeliveryLine[] {
  return lines.map((line) => (line.line_id === lineId ? { ...line, ...changes } : line));
}

export function findDeliveryProblem(included: readonly DeliveryLine[]): string | null {
  if (included.length === 0) return 'Tick at least one reagent that arrived.';
  if (included.some((line) => line.lot_no.trim().length === 0)) {
    return 'Every line needs a lot number. Use — for a consumable with no vendor batch.';
  }
  return null;
}

export function buildReceiptBody(
  header: DeliveryHeader,
  included: readonly DeliveryLine[],
): CreateReceiptInput {
  return {
    received_at: header.received_at || null,
    invoice_no: header.invoice_no || null,
    delivery_note: header.delivery_note || null,
    note: header.note || null,
    lines: included.map((line) => ({
      item_id: line.item_id,
      quantity_packs: line.quantity_packs,
      lot_no: line.lot_no.trim(),
      expiry_at: line.expiry_at || null,
      pack_price: line.pack_price,
    })),
  };
}

export function calculateInvoiceValue(included: readonly DeliveryLine[]): number {
  return included.reduce((sum, line) => sum + line.quantity_packs * line.pack_price, 0);
}
